'use client'

import { CSSProperties, useMemo } from 'react'
import { IconEye, IconFileDownload } from '@tabler/icons-react'
import clsx from 'clsx'

import { CustomerTypes } from 'modules/customer'
import Button from 'shared/ui/Button'
import { ButtonVariants } from 'shared/ui/Button/Button.enums'
import Tooltip from 'shared/ui/Tooltip/Tooltip'
import { theme } from 'shared/ui/utils/designTokens'
import { parseDateToDDMMYYYY } from 'shared/utils/date'
import { PATHS } from 'shared/utils/paths'
import { isNotNilOrEmpty } from 'shared/utils/ramda'
import { addTestId } from 'shared/utils/tests'

import EditSaleOrderButton from '../submodules/editSaleOrder/components/EditSaleOrderButton'
import { isEditSaleOrderEnabled } from '../submodules/editSaleOrder/utils'
import { downloadSaleOrderDocument } from '../submodules/invoices/utils'
import { SaleOrder } from '../types'
import { isAwaitingPaymentSaleOrder } from '../utils/saleOrders'

import OrderReturnStatusBadge from './ProfileSaleOrderReturnStatusBadge'
import SaleOrderStatus from './SaleOrderStatus'

import styles from './ProfileSaleOrdersListItemWeb.module.scss'

const getCellStyle = (width: string) =>
  ({ '--cell-width': width }) as CSSProperties

interface ProfileSaleOrdersListItemWebProps {
  order: SaleOrder
  user: CustomerTypes
  handlePaymentStart: (order: SaleOrder) => Promise<void>
  handleRedirect: (path: string) => () => void
  disabledDocuments?: boolean
  token?: string
  columnWidths: string[]
}

export default function ProfileSaleOrdersListItemWeb({
  order,
  handlePaymentStart,
  handleRedirect,
  disabledDocuments = false,
  token,
  columnWidths
}: ProfileSaleOrdersListItemWebProps) {
  const documentsList = useMemo(
    () => [...order.invoices, ...order.receipts],
    [order.invoices, order.receipts]
  )
  const hasDocuments = isNotNilOrEmpty(documentsList)
  const isAwaitingPayment = isAwaitingPaymentSaleOrder(order)

  const showDownloadButton = hasDocuments && token && !isAwaitingPayment

  const showDownloadButtonTooltip = !showDownloadButton && !isAwaitingPayment

  return (
    <tr className={styles.row}>
      <td
        className={styles.cell}
        width={columnWidths[0]}
        style={getCellStyle(columnWidths[0])}
      >#{order.code}</td>
      <td
        className={styles.cell}
        width={columnWidths[1]}
        style={getCellStyle(columnWidths[1])}
      >
        {parseDateToDDMMYYYY(order.createdAt)}
      </td>
      <td
        className={styles.cell}
        width={columnWidths[2]}
        style={getCellStyle(columnWidths[2])}
        dangerouslySetInnerHTML={{
          __html: order.paymentValue.formatted
        }}
      />
      <td
        className={styles.cell}
        width={columnWidths[3]}
        style={getCellStyle(columnWidths[3])}
      >
        <SaleOrderStatus status={order.status} />
      </td>
      <td
        className={styles.cell}
        width={columnWidths[4]}
        style={getCellStyle(columnWidths[4])}
      >
        {order.returnStatus && (
          <OrderReturnStatusBadge status={order.returnStatus} />
        )}
      </td>
      <td
        className={clsx(styles.cell, styles.reTryPaymentButton)}
        width={columnWidths[5]}
        style={getCellStyle(columnWidths[5])}
      >
        {isAwaitingPayment && (
          <Button
            size='small'
            variant={ButtonVariants.primary}
            onClick={() => handlePaymentStart(order)}
            {...addTestId('profile_order_retry_payment_button')}
          >
            Zapłać
          </Button>
        )}
      </td>
      <td className={styles.orderIcons}>
        {isEditSaleOrderEnabled(order) && (
          <EditSaleOrderButton size='small' order={order} />
        )}
        {showDownloadButton &&
          !disabledDocuments &&
          documentsList.map((document, index) => (
            <IconFileDownload
              key={`${index}`}
              id={`order_document_button_${index}`}
              size={25}
              onClick={() =>
                downloadSaleOrderDocument(order, document, token || '')
              }
            />
          ))}
        {showDownloadButtonTooltip && !disabledDocuments && (
          <Tooltip
            id='profile-order-download-button-tooltip'
            title='Dowód zakupu będzie dostępny po dostarczeniu zamówienia'
          >
            <IconFileDownload size={25} color={theme.colors.text.subtle} />
          </Tooltip>
        )}
        <IconEye
          size={25}
          onClick={handleRedirect(`${PATHS.profileOrders}?order=${order.id}`)}
        />
      </td>
    </tr>
  )
}
